import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { canAdminLibrary, canImportLibrary, libraryError } from '@/lib/libraryAccess';
import { isPlatformAdmin } from '@/lib/platformAdmin';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Library, Search, Package, CheckCircle2 } from 'lucide-react';
import { LibraryEmptyState, LibraryErrorState, LibraryLoading } from '@/components/forms/LibraryStates';
import { useLibraryBranding } from '@/lib/libraryBranding';
import { categoryFilterOptions, displayCategory } from '@/config/libraryCategories';

const MODULE_TYPES = ['NDIS_ASSESSMENT', 'INCIDENT_REPORT', 'ONBOARDING', 'STAFF_COMPLIANCE', 'SHIFT_CHECKLIST', 'SIL_AUDIT', 'RECRUITMENT', 'PARTICIPANT_INTAKE', 'GENERAL'];
const PAGE_SIZE = 20;
const CATEGORY_PAGE_SIZE = 100; // server max; a display-category filter is applied client-side over the whole (small) library

export default function FormLibraryPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const brand = useLibraryBranding();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [moduleType, setModuleType] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imported, setImported] = useState({}); // `${pack_id}/${library_key}` → count of org copies

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: category ? 1 : page, pageSize: category ? CATEGORY_PAGE_SIZE : PAGE_SIZE });
      if (q.trim()) params.set('q', q.trim());
      if (moduleType) params.set('module_type', moduleType);
      const { data } = await api.get(`/forms/library?${params}`);
      const rows = data.items || [];
      const shown = category ? rows.filter(it => displayCategory(it.library_key) === category) : rows;
      setItems(shown); setTotal(category ? shown.length : (data.total || 0));
    } catch (e) { setError(libraryError(e)); setItems([]); }
    setLoading(false);
  }, [page, q, moduleType, category]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/forms/library/updates').then(({ data }) => {
      const m = {};
      (data.items || []).forEach(u => { const k = `${u.pack_id}/${u.library_key}`; m[k] = (m[k] || 0) + 1; });
      setImported(m);
    }).catch(() => setImported({}));
  }, []);

  const open = (id) => navigate(`/forms/library/${id}`);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="form-library-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Library className="w-6 h-6" />Template Library</h1>
          <p className="text-muted-foreground mt-1 text-sm">NDIS Audit Readiness Centre · read-only masters you can import into your own Form Templates as editable drafts.</p>
        </div>
        <div className="flex items-center gap-2">
          {isPlatformAdmin(user) && canAdminLibrary(hasPermission) && (
            <Button variant="outline" onClick={() => navigate('/forms/library/packs')} data-testid="library-packs-btn"><Package className="w-4 h-4 mr-2" />Library Packs</Button>
          )}
          <Button variant="outline" onClick={() => navigate('/forms/templates')} data-testid="library-my-templates-btn">My Form Templates</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input aria-label="Search library" placeholder="Search library…" className="pl-9" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} data-testid="library-search" />
        </div>
        <Select value={moduleType || 'ALL'} onValueChange={v => { setModuleType(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-52" aria-label="Filter by module type" data-testid="library-module-filter"><SelectValue placeholder="All module types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All module types</SelectItem>
            {MODULE_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || 'ALL'} onValueChange={v => { setCategory(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-60" aria-label="Filter by category" data-testid="library-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {categoryFilterOptions().map(c => <SelectItem key={c} value={c} data-testid={`library-category-option-${c.replace(/[^A-Za-z]+/g, '-').toLowerCase()}`}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground" data-testid="library-total">{total} active master{total === 1 ? '' : 's'}</span>
      </div>

      {loading ? <LibraryLoading /> : error ? <LibraryErrorState error={error} onRetry={load} /> : items.length === 0 ? (
        <LibraryEmptyState title="No library masters available" hint={q || moduleType || category ? 'Try a different search, module type or category.' : 'Nothing has been published to the Template Library for your organisation yet.'} />
      ) : (
        <ul className="space-y-2" data-testid="library-list">
          {items.map(it => {
            const copies = imported[`${it.pack_id}/${it.library_key}`] || 0;
            return (
              <li key={it.id}>
                <Card className="hover:shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-primary/40" data-testid={`library-row-${it.id}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-4">
                    <button type="button" className="flex items-center gap-4 flex-1 min-w-0 text-left rounded-md outline-none" onClick={() => open(it.id)} data-testid={`library-open-${it.id}`}>
                      <Library className="w-5 h-5 text-muted-foreground shrink-0" />
                      <span className="min-w-0">
                        <span className="block font-medium text-sm truncate">{it.name}</span>
                        <span className="block text-xs text-muted-foreground" data-testid={`library-row-subtitle-${it.id}`}>{brand.packLabel(it.pack_version)} · {it.module_type} · <span data-testid={`library-row-category-${it.id}`}>{displayCategory(it.library_key)}</span></span>
                      </span>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {it.classification && <Badge variant="outline" className="text-xs">{it.classification}</Badge>}
                      {copies > 0 && <Badge variant="secondary" className="text-xs" data-testid={`library-imported-${it.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Imported ×{copies}</Badge>}
                      {canImportLibrary(hasPermission) && <Button size="sm" variant="outline" className="h-8" onClick={() => open(it.id)} data-testid={`library-import-${it.id}`}>Import…</Button>}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {!category && total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="library-prev">Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="library-next">Next</Button>
        </div>
      )}
    </div>
  );
}
